/**
 * blog-utils.ts
 * Data Transformers & Content Parsers for the Blog Engine
 */

export interface Heading {
  id: string;
  text: string;
  level: number;
}

export interface FormattedBlogPost {
  id: number;
  title: string;
  slug: string;
  author: string;
  publishedAt: string;
  content: string;
  excerpt: string;
  tags: string[];
  category?: string;
  coverImage?: string;
  shareCount: number;
  views: number;
  stats: {
    minutes: number;
    words: number;
  };
  reactions: {
    fire: number;
    clap: number;
    idea: number;
  };
}

/**
 * Technical Reading Time Calculator
 * Adjusted for code-heavy content (200 wpm)
 */
export function calculateReadingStats(content: string) {
  const words = content.trim().split(/\s+/).length;
  const wordsPerMinute = 200; 
  const minutes = Math.max(1, Math.ceil(words / wordsPerMinute));
  return { minutes, words };
}

/**
 * Transforms Prisma raw data into a flat, component-friendly object.
 * Handles the aggregation of reaction and analytic arrays.
 */
export function formatBlogPost(post: any): FormattedBlogPost {
  const content = post.content ?? "";
  
  // Aggregate stats from potential multiple analytic records
  const totalShares = post.analytics?.reduce((acc: number, curr: any) => acc + (curr.shares ?? 0), 0) ?? 0;
  // Fallback to post.views if analytics record doesn't exist yet
  const totalViews = post.analytics?.reduce((acc: number, curr: any) => acc + (curr.views ?? 0), 0) ?? post.views ?? 0;

  // Aggregate reactions across the array
  const reactions = post.reactions?.reduce((acc: any, curr: any) => ({
    fire: acc.fire + (curr.fire ?? 0),
    clap: acc.clap + (curr.clap ?? 0),
    idea: acc.idea + (curr.idea ?? 0),
  }), { fire: 0, clap: 0, idea: 0 }) ?? { fire: 0, clap: 0, idea: 0 };

  return {
    id: post.id,
    title: post.title,
    slug: post.slug,
    author: post.author,
    content: content,
    publishedAt:
      post.publishedAt instanceof Date
        ? post.publishedAt.toISOString()
        : post.publishedAt ?? new Date().toISOString(),

    excerpt: post.excerpt || (content.length > 160 ? `${content.slice(0, 157)}...` : content),
    tags: post.tags ?? [],
    category: post.category ?? undefined,
    coverImage: post.coverImage ?? undefined,

    stats: calculateReadingStats(content),
    shareCount: totalShares,
    views: totalViews,
    reactions: reactions,
  };
}

/**
 * Generates a Table of Contents list from Markdown.
 * Creates URL-safe IDs for section jumping.
 */
export function parseHeadings(markdown: string) {
  const headings: Heading[] = [];
  // Matches #, ##, and ### headings
  const headingRegex = /^(#{1,3})\s+(.+)$/gm;

  let match;
  while ((match = headingRegex.exec(markdown)) !== null) {
    const level = match[1].length;
    const text = match[2].trim();
    
    // Create linkable ID: "Hello World!" -> "hello-world"
    const id = text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-')  // Replace spaces/underscores with hyphens
      .replace(/^-+|-+$/g, '');  // Trim leading/trailing hyphens
    
    headings.push({ id, text, level });
  }

  return { contentWithIds: markdown, headings };
}